// Polling framework.
//
// Standardises the shape every background poller takes: interval +
// jitter, visibility / focus gates, API-temperature back-off, optional
// tab-lock ownership, optional feature-flag requirement. Each poller
// describes its requirements declaratively; the framework handles the
// scheduling + guard-checks + error isolation.
//
// Every future poller (webhook relay, enrichment backfill, scheduled-
// send dispatcher, audit-retention fallback) registers via
// `createPollingManager`. No more ad-hoc `setInterval` scattered across
// the codebase.
//
// Architecture composition:
//   - ADR 0021 API temperature — skip when `shouldBackOff` is true
//   - ADR 0022 Jotai — status exposed via `atomPollingStatus(name)`
//   - ADR 0023 Tab lock — optional `requireLock` for cross-tab coordination
//   - ADR 0005 Feature flags — optional `requiredFeatures`
//
// ADR: docs/adr/0024-polling-framework.md

import { createLogger } from './logger'
import { isFeatureOn, type FeatureFlag } from './features'
import {
  HEARTBEAT_INTERVAL_MS,
  heartbeatTabLock,
  isLockHeldByOtherTab,
  releaseTabLock,
  tryAcquireTabLock,
} from './tabLock'
import { getApiTemperature, isApiCooling } from './apiTemperature'
import { jotaiStore } from './jotaiStore'
import { _setPollingStatus } from './atoms/atomPolling'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PollingOptions {
  /** Unique name. Used for logging, atom key, tab-lock key default. */
  name: string

  /** The work. Errors are caught + logged — never propagate. Returning a
   * promise that resolves when the work is done is the contract. */
  onTick: () => Promise<void>

  /** Base interval in ms. 60_000 = one minute. */
  interval: number

  /** Randomise each tick's delay by ±jitter ms. Avoids thundering-herd
   * when N tabs or pollers align. Defaults to `interval / 10`. */
  jitter?: number

  /**
   * Delay before the *first* tick. Defaults to `interval` — i.e. the
   * first tick fires one interval after `start()`, not immediately.
   * Set to 0 to fire immediately; set to a random value in
   * [0, interval) at startup to stagger multiple pollers.
   */
  offset?: number

  /** Required feature flags — all must be on for a tick to fire. */
  requiredFeatures?: FeatureFlag[]

  /**
   * Tab-lock key the poller must hold to tick. When set, the poller
   * attempts to acquire on start and on each tick; it ticks only if
   * successful. Heartbeat is the framework's job.
   *
   * Default: no lock required (every tab ticks independently — rare;
   * use explicitly for "single-tab-only" work).
   */
  requireLock?: string

  /** Skip ticks when `document.visibilityState !== 'visible'`.
   * Default true. */
  requireVisible?: boolean

  /** Skip ticks when `document.hasFocus()` is false. Default false —
   * many pollers want to run even when the window isn't focused. */
  requireFocus?: boolean

  /** Skip ticks when `shouldBackOff` is true (temperature MEDIUM+ or
   * cooling after a 429). Default true. */
  backoffOnTemperature?: boolean
}

export interface PollingManager {
  /** Start the polling loop. Idempotent. */
  start(): void
  /** Stop the loop + release any held lock. */
  stop(): void
  /** Fire a tick immediately (bypasses interval, still respects gates). */
  tickNow(): Promise<void>
  /** Whether the loop is currently running. */
  isRunning(): boolean
}

export interface PollingStatus {
  running: boolean
  lastTickAt: number | null
  lastError: string | null
  skippedReason: string | null
}

// ─── Factory ─────────────────────────────────────────────────────────────────

export function createPollingManager(options: PollingOptions): PollingManager {
  const {
    name,
    onTick,
    interval,
    jitter = Math.floor(interval / 10),
    offset = interval,
    requiredFeatures = [],
    requireLock,
    requireVisible = true,
    requireFocus = false,
    backoffOnTemperature = true,
  } = options

  const log = createLogger(`polling:${name}`)

  let running = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let tickInFlight = false

  const status: PollingStatus = {
    running: false,
    lastTickAt: null,
    lastError: null,
    skippedReason: null,
  }
  function publish(update: Partial<PollingStatus>): void {
    Object.assign(status, update)
    _setPollingStatus(name, { ...status })
  }

  // ─── Gate checks ─────────────────────────────────────────────────────────
  //
  // Each returns a "skip" reason string when the tick should NOT fire, or
  // null when everything is go. We report the first failing reason so the
  // debug tools can see why a poller is idle.

  function checkVisibility(): string | null {
    if (!requireVisible) return null
    if (typeof document === 'undefined') return null
    if (document.visibilityState !== 'visible') return 'not-visible'
    return null
  }

  function checkFocus(): string | null {
    if (!requireFocus) return null
    if (typeof document === 'undefined') return null
    if (!document.hasFocus()) return 'not-focused'
    return null
  }

  function checkFeatures(): string | null {
    for (const flag of requiredFeatures) {
      if (!isFeatureOn(flag)) return `feature-off:${flag}`
    }
    return null
  }

  function checkTemperature(): string | null {
    if (!backoffOnTemperature) return null
    if (isApiCooling()) return 'api-cooling'
    const t = getApiTemperature()
    if (t === 'MEDIUM' || t === 'HIGH') return `temp-${t.toLowerCase()}`
    return null
  }

  function checkLock(): string | null {
    if (!requireLock) return null
    if (isLockHeldByOtherTab(requireLock)) return 'lock-held-by-other-tab'
    // Attempt acquire — if we can't (race), skip this tick.
    if (!tryAcquireTabLock(requireLock)) return 'lock-acquire-failed'
    return null
  }

  // ─── The tick ────────────────────────────────────────────────────────────

  async function doTick(): Promise<void> {
    if (tickInFlight) {
      publish({ skippedReason: 'tick-in-flight' })
      return
    }

    // Gates in cheap-first order: vis/focus before temperature before lock.
    const gate =
      checkVisibility() ??
      checkFocus() ??
      checkFeatures() ??
      checkTemperature() ??
      checkLock()

    if (gate) {
      publish({ skippedReason: gate })
      return
    }

    tickInFlight = true
    publish({ skippedReason: null })

    try {
      await onTick()
      publish({
        lastTickAt: Date.now(),
        lastError: null,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      log.error('tick failed', e)
      publish({
        lastTickAt: Date.now(),
        lastError: msg,
      })
    } finally {
      tickInFlight = false
    }
  }

  // ─── Scheduling ──────────────────────────────────────────────────────────

  function nextDelay(): number {
    // Uniform random in [-jitter, +jitter] around interval.
    const randomJitter = Math.floor((Math.random() * 2 - 1) * jitter)
    return Math.max(0, interval + randomJitter)
  }

  function scheduleNext(delay: number): void {
    if (!running) return
    timer = setTimeout(async () => {
      timer = null
      await doTick()
      if (running) scheduleNext(nextDelay())
    }, delay)
  }

  function startHeartbeat(): void {
    if (!requireLock) return
    if (heartbeatTimer) return
    heartbeatTimer = setInterval(() => {
      heartbeatTabLock(requireLock)
    }, HEARTBEAT_INTERVAL_MS)
  }

  function stopHeartbeat(): void {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  const manager: PollingManager = {
    start(): void {
      if (running) return
      running = true
      publish({ running: true })
      startHeartbeat()
      scheduleNext(Math.max(0, offset))
      log.debug(`started (interval=${interval}ms jitter=${jitter}ms)`)
    },

    stop(): void {
      if (!running) return
      running = false
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      stopHeartbeat()
      if (requireLock) releaseTabLock(requireLock)
      publish({ running: false, skippedReason: null })
      log.debug('stopped')
    },

    async tickNow(): Promise<void> {
      await doTick()
    },

    isRunning(): boolean {
      return running
    },
  }

  // Publish initial status so consumer atoms see an entry even before start.
  publish({})

  return manager
}

// Re-export useful references for callers.
export { jotaiStore }
